import * as React from 'react';

const AuthOverlayContext = React.createContext(null);

/**
 * API exposée :
 *  openLogin({ onSuccess }?)
 *  openRegister({ onSuccess }?)
 *  openForgot()
 *  openReset({ token }?)
 *  closeAll()
 */
export function AuthOverlayProvider({ children }) {
  const [state, setState] = React.useState({
    loginOpen: false,
    registerOpen: false,
    forgotOpen: false,
    resetOpen: false,
    resetToken: '',
    onSuccess: null, // callback (login/register réussi) — laissé pour compat si besoin
  });

  const closeAll = React.useCallback(() => {
    setState(s => ({
      ...s,
      loginOpen: false,
      registerOpen: false,
      forgotOpen: false,
      resetOpen: false,
      onSuccess: null,
      resetToken: ''
    }));
  }, []);

  const openLogin = React.useCallback((opts = {}) => {
    setState({
      loginOpen: true,
      registerOpen: false,
      forgotOpen: false,
      resetOpen: false,
      resetToken: '',
      onSuccess: opts.onSuccess || null,
    });
  }, []);

  const openRegister = React.useCallback((opts = {}) => {
    setState({
      loginOpen: false,
      registerOpen: true,
      forgotOpen: false,
      resetOpen: false,
      resetToken: '',
      onSuccess: opts.onSuccess || null,
    });
  }, []);

  const openForgot = React.useCallback(() => {
    setState(s => ({
      ...s,
      loginOpen: false,
      registerOpen: false,
      forgotOpen: true,
      resetOpen: false,
      resetToken: ''
    }));
  }, []);

  const openReset = React.useCallback((opts = {}) => {
    setState(s => ({
      ...s,
      loginOpen: false,
      registerOpen: false,
      forgotOpen: false,
      resetOpen: true,
      resetToken: opts.token || ''
    }));
  }, []);

  const value = React.useMemo(() => ({
    ...state,
    openLogin,
    openRegister,
    openForgot,
    openReset,
    closeAll,
    setOnSuccess: (fn) => setState(s => ({ ...s, onSuccess: fn })),
  }), [state, openLogin, openRegister, openForgot, openReset, closeAll]);

  return (
    <AuthOverlayContext.Provider value={value}>
      {children}
    </AuthOverlayContext.Provider>
  );
}

export function useAuthOverlay() {
  const ctx = React.useContext(AuthOverlayContext);
  if (!ctx) throw new Error('useAuthOverlay must be used within <AuthOverlayProvider>');
  return ctx;
}